import {LeanDocument} from 'mongoose';
import {CourseManagerAPI} from '@/classes/api/course-manager.api.class';
import {CourseStates} from '@/enums/CourseStates.enum';
import {CourseDocument} from '@/documentTypes/course-manager/Course.document';
import {PickerItem, PickerSlot} from './entityPicker.types';
import {formatSlot, idOf, isValidSlot, overlapsSlot, plural} from './entityPicker.helper';
import {activeStudentCount} from '@/helpers/courseStudentCount.helper';

/** Planning assumption of the school: on average at most 6 children per course */
export const AVERAGE_MAX_CHILDREN_PER_COURSE = 6;

type CourseLite = Pick<LeanDocument<CourseDocument>, '_id' | 'customId' | 'dayOfWeek' | 'timeRange' | 'room' | 'studentItems'>;

export {activeStudentCount};

/** Free seats of courses assuming max. 6 children per course (never negative per course) */
export function estimatedFreeSeats(courses: Array<Pick<CourseLite, 'studentItems'>>): number {
    return courses.reduce((sum, course) => sum + Math.max(0, AVERAGE_MAX_CHILDREN_PER_COURSE - activeStudentCount(course)), 0);
}

async function loadRoomCourses(roomIds: string[]): Promise<CourseLite[] | null> {
    if (!roomIds.length) {
        return [];
    }
    try {
        const resp = await CourseManagerAPI.find({
            filter: {
                room: roomIds,
                endsAfter: new Date(),
                courseState: [CourseStates.ACTIVE],
            },
            fields: ['customId', 'dayOfWeek', 'timeRange', 'room', 'studentItems'],
            skipPagination: true,
        } as any);
        return resp.courses as CourseLite[];
    } catch (e) {
        // No read access on courses: no hint
        return null;
    }
}

function slotContext(courses: CourseLite[], slot: PickerSlot): string {
    const atSlot = courses.filter((course) => overlapsSlot(course, slot));
    if (!atSlot.length) {
        return `${formatSlot(slot)}: kein Kurs im Raum`;
    }
    const ids = atSlot.map((course) => course.customId).filter(Boolean).join(', ');
    return `${formatSlot(slot)}: ${ids || plural(atSlot.length, 'Kurs', 'Kurse')} · noch ~${estimatedFreeSeats(atSlot)} frei (Ø ${AVERAGE_MAX_CHILDREN_PER_COURSE})`;
}

function overallContext(courses: CourseLite[]): string {
    if (!courses.length) {
        return 'keine aktiven Kurse im Raum';
    }
    return `${plural(courses.length, 'Kurs', 'Kurse')} · noch ~${estimatedFreeSeats(courses)} frei (Ø ${AVERAGE_MAX_CHILDREN_PER_COURSE})`;
}

/**
 * Soft hint how many children still fit into the courses of a room – at the dialog's weekday/time
 * if known, otherwise over all active courses of the room. One batched course query; never disables.
 */
export async function enrichRoomItems(items: PickerItem[], slot: PickerSlot | null): Promise<PickerItem[]> {
    const courses = await loadRoomCourses(items.map((item) => String(item.value)));
    if (!courses) {
        return items;
    }
    const validSlot = isValidSlot(slot) ? slot : null;
    return items.map((item) => {
        const own = courses.filter((course) => idOf(course.room) === String(item.value));
        return {
            ...item,
            context: [validSlot ? slotContext(own, validSlot) : overallContext(own), ...(item.context || [])],
        };
    });
}
